import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', tour_interest: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Manual validation
    if (!form.name.trim() || !form.email.trim()) {
      alert('Please fill in your name and email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      alert('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please call us at 1-877-741-8390.');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <section id="contact" className="section-padding bg-white" data-testid="contact-section">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="font-bebas text-4xl tracking-tighter uppercase mb-3">Message Received!</h2>
            <p className="font-outfit text-base text-[#4A4A4A] mb-6">
              Our team will get back to you shortly. For immediate assistance, call us 24/7.
            </p>
            <a href="tel:+18777418390" className="btn-primary px-8 py-3 text-sm" data-testid="contact-success-call">
              <Phone className="w-4 h-4" /> CALL 1-877-741-8390
            </a>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="section-padding bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FF007A] mb-2">We Are Here to Help</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">
            Get in Touch
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-brutal p-5" data-testid="contact-phone-card">
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-5 h-5 text-[#FF007A]" />
                <h3 className="font-bebas text-lg tracking-tight uppercase">24/7 Reservation Line</h3>
              </div>
              <a href="tel:+18777418390" className="font-outfit font-bold text-lg block hover:text-[#FF007A] transition-colors">1-877-741-8390</a>
              <span className="font-outfit text-xs text-[#4A4A4A] uppercase tracking-wider">Toll-Free USA & Canada</span>
              <a href="tel:+17029415144" className="font-outfit font-bold text-base block mt-2 hover:text-[#FF007A] transition-colors">+1 (702) 941-5144</a>
              <span className="font-outfit text-xs text-[#4A4A4A] uppercase tracking-wider">International Callers</span>
            </div>
            <div className="card-brutal p-5" data-testid="contact-email-card">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5 text-[#00E5FF]" />
                <h3 className="font-bebas text-lg tracking-tight uppercase">Email Us</h3>
              </div>
              <a href="mailto:rsvp@vegas2hollywood.com" className="font-outfit font-bold hover:text-[#FF007A] transition-colors">rsvp@vegas2hollywood.com</a>
            </div>
            <div className="card-brutal p-5" data-testid="contact-hours-card">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-[#FFD600]" />
                <h3 className="font-bebas text-lg tracking-tight uppercase">Hours</h3>
              </div>
              <p className="font-outfit font-bold">24/7 — We Never Close</p>
              <p className="font-outfit text-sm text-[#4A4A4A]">Always here for your adventure planning</p>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="lg:col-span-3 card-brutal p-6 sm:p-8"
            data-testid="contact-form"
            noValidate
          >
            <h3 className="font-bebas text-2xl tracking-tight uppercase mb-5">Send Us a Message</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="font-outfit text-sm font-bold uppercase tracking-wider block mb-1">Full Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full border-2 border-black rounded-lg px-4 py-2.5 font-outfit text-sm focus:outline-none focus:ring-2 focus:ring-[#FF007A]"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="font-outfit text-sm font-bold uppercase tracking-wider block mb-1">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full border-2 border-black rounded-lg px-4 py-2.5 font-outfit text-sm focus:outline-none focus:ring-2 focus:ring-[#FF007A]"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="font-outfit text-sm font-bold uppercase tracking-wider block mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full border-2 border-black rounded-lg px-4 py-2.5 font-outfit text-sm focus:outline-none focus:ring-2 focus:ring-[#FF007A]"
                  placeholder="+1 (555) 000-0000"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="font-outfit text-sm font-bold uppercase tracking-wider block mb-1">Tour Interest</label>
                <select
                  value={form.tour_interest}
                  onChange={e => setForm({ ...form, tour_interest: e.target.value })}
                  className="w-full border-2 border-black rounded-lg px-4 py-2.5 font-outfit text-sm focus:outline-none focus:ring-2 focus:ring-[#FF007A] bg-white"
                  data-testid="contact-tour-select"
                >
                  <option value="">Select a tour</option>
                  <option value="vegas-to-hollywood">Vegas to Hollywood - $215</option>
                  <option value="private-tour">Private Hollywood Tour</option>
                  <option value="group-tour">Group Tour (15+)</option>
                  <option value="other">Other Inquiry</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="font-outfit text-sm font-bold uppercase tracking-wider block mb-1">Message</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                className="w-full border-2 border-black rounded-lg px-4 py-2.5 font-outfit text-sm focus:outline-none focus:ring-2 focus:ring-[#FF007A] resize-none"
                placeholder="Tell us about your group, preferred dates, or any questions..."
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full px-8 py-3 text-sm"
              data-testid="contact-submit-btn"
            >
              <Send className="w-4 h-4" />
              {loading ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
            <p className="font-outfit text-xs text-[#4A4A4A] mt-3 text-center">
              By submitting, you agree to receive tour information from Vegas to Hollywood Tours. You may unsubscribe at any time.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
